import { I18n } from "@assets/strings";
import { Set } from "immutable";
import React, { JSX, useEffect, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { allCountrycodes, countries, Country, countryCodes } from "@domain/currentProfile/countryCode";
import { OrganizationDraft } from "@domain/organization/organizationDraft";
import { OrganizationSubType, subTypesByTypes } from "@domain/organization/organizationType";
import { useAsync } from "@utils/hooks";
import { isEmail, isPhoneNumber } from "@utils/regex";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryBold } from "@ui/components/containers/primary";
import { CountryDatalist } from "@ui/components/inputs/countryDatalist";
import { DatePicker } from "@ui/components/inputs/datePicker";
import { Selecter } from "@ui/components/inputs/selecter";
import { SimpleTextInput, SimpleTextInputProps } from "@ui/components/inputs/simpleTextInput";
import { stack } from "@ui/components/layout";
import { EditableProfileImage } from "@ui/components/profile/editableProfileImage";

interface OrganizationEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	initialOrganization: OrganizationDraft;
	organizationId?: string;
	onSave?: () => void;
}
export const OrganizationEditor: React.FC<OrganizationEditorProps> = ({
	initialOrganization,
	organizationId,
	onSave,
	...htmlProps
}) => {
	const { currentProfileService, organizationService, trackingService } = useServices();
	const [organization, setOrganization] = useState<Omit<OrganizationDraft, "countryCode">>(initialOrganization);
	const [country, setCountry] = useState<Country | undefined>(
		initialOrganization.countryCode ? countries[initialOrganization.countryCode] : undefined
	);
	const [missingFields, setMissingFields] = useState<Set<keyof OrganizationDraft>>(Set());
	const [invalidFields, setInvalidFields] = useState<Set<keyof OrganizationDraft>>(Set());
	const currentYear = new Date().getFullYear();
	const history = useHistory();

	const fieldProps = (
		field: keyof Omit<
			OrganizationDraft,
			| "banner"
			| "avatar"
			| "categories"
			| "beginDate"
			| "tracks"
			| "presentation"
			| "assets"
			| "countryCode"
			| "presentationAsset"
			| "contentCountList"
		>
	): SimpleTextInputProps => ({
		value: organization[field] ?? "",
		onChange: (v) => {
			setOrganization((c) => ({ ...c, [field]: v }));
			setMissingFields((c) => c.delete(field));
			setInvalidFields((c) => c.delete(field));
		},
		placeholder: I18n.t(`organization.form.${field}.placeholder`),
		error: missingFields.has(field) ? I18n.t("form.missing") : invalidFields.has(field) ? I18n.t("form.invalid") : "",
	});

	const { run, loading, error } = useAsync(
		() => organizationService.save({ ...organization, countryCode: country && countryCodes[country] }, organizationId),
		[organization, country, organizationId]
	);

	useEffect(() => {
		if (error) {
			createToast(I18n.t("error"), I18n.t("organization.save.error"), "error");
		}
	}, [error]);

	const getFormItem = (
		field: keyof Omit<
			OrganizationDraft,
			| "banner"
			| "avatar"
			| "categories"
			| "beginDate"
			| "tracks"
			| "presentation"
			| "assets"
			| "countryCode"
			| "presentationAsset"
			| "contentCountList"
		>
	): JSX.Element => (
		<InfoEdition>
			<PrimaryBold>{I18n.t(`organization.form.${field}.title`)}</PrimaryBold>
			<SimpleTextInput {...fieldProps(field)} />
		</InfoEdition>
	);

	return (
		<Container {...htmlProps}>
			{getFormItem("primaryName")}
			<EditableProfileImage
				type="banner"
				fallbackType={organization.type}
				image={organization.banner}
				label={I18n.t(organization.banner ? "organization.banner.edit" : "organization.banner.add")}
				onUpload={(media) => setOrganization((current) => ({ ...current, banner: media }))}
				onDelete={() => setOrganization((current) => ({ ...current, banner: undefined }))}
				canBeEmpty
			/>
			<EditableProfileImage
				type="avatar"
				fallbackType={organization.type}
				image={organization.avatar}
				label={I18n.t(organization.banner ? "organization.logo.edit" : "organization.logo.add")}
				onUpload={(media) => setOrganization((current) => ({ ...current, avatar: media }))}
				onDelete={() => setOrganization((current) => ({ ...current, avatar: undefined }))}
				canBeEmpty
			/>
			<InfoEdition>
				<PrimaryBold>{I18n.t("organization.form.creationDate.title")}</PrimaryBold>
				<StyledDatePicker
					hideMonth
					value={organization.beginDate ? new Date(organization.beginDate) : undefined}
					onChange={(date) => setOrganization((current) => ({ ...current, beginDate: date }))}
					minYear={currentYear - 1000}
					maxYear={currentYear + 1}
				/>
			</InfoEdition>
			<InfoEdition>
				<PrimaryBold>{I18n.t("organization.form.country.title")}</PrimaryBold>
				<CountryEditor
					id="countries"
					value={country}
					placeholder={I18n.t("organization.form.country.placeholder")}
					onChange={(value) => {
						setInvalidFields((c) => c.delete("countryCode"));
						setCountry(value as Country);
					}}
					options={allCountrycodes}
					invalid={invalidFields.has("countryCode")}
				/>
			</InfoEdition>
			<InfoEdition>
				<PrimaryBold>{I18n.t(`organization.form.${organization.type.toLowerCase()}Type.title`)}</PrimaryBold>
				<TypeSelecter
					missing={missingFields.has("subType")}
					placeholder={I18n.t(`organization.form.${organization.type.toLowerCase()}Type.placeholder`)}
					value={organization.subType}
					onChange={(value) => setOrganization((current) => ({ ...current, subType: value as OrganizationSubType }))}
					options={subTypesByTypes[organization.type].map((subType) => ({
						value: subType,
						label: I18n.t(`organization.type.${subType}`),
					}))}
				/>
			</InfoEdition>
			<Form>
				{getFormItem("description")}
				{getFormItem("email")}
				{getFormItem("address")}
				{getFormItem("phoneNumber")}
				{getFormItem("webSite")}
			</Form>
			<LoadingButton
				loading={loading}
				onClick={async () => {
					const missing = Set(
						(["primaryName", "description", "email", "subType"] as const).filter((field) => !organization[field])
					);

					const countryInvalid = !country || !(country in countryCodes) ? "countryCode" : null;
					const emailInvalid = !organization.email || !isEmail(organization.email) ? "email" : null;
					const phoneInvalid =
						organization.phoneNumber && !isPhoneNumber(organization.phoneNumber) ? "phoneNumber" : null;
					const invalid = Set(
						[emailInvalid, phoneInvalid, countryInvalid].filter(Boolean) as (keyof OrganizationDraft)[]
					);
					setMissingFields(missing);
					setInvalidFields(invalid);
					if (missing.size > 0 || invalid.size > 0) {
						return;
					}
					const newOrganization = await run();
					if (!organizationId) {
						trackingService.track(`organization ${initialOrganization.type.toLocaleLowerCase()} / create`);
					}
					createToast(I18n.t("success"), I18n.t("organization.save.success"), "success");
					onSave?.();
					history.push(Path.Organization(newOrganization, "presentation"));
					currentProfileService.setAlias(newOrganization);
				}}
			>
				{I18n.t(organizationId ? "organization.save.button" : "organization.create.button")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled.div`
	${gap(35)};

	> button {
		margin: 0 auto;
	}
`;

const Form = styled.div`
	align-self: stretch;
	${gap(28)}
	display: flex;
	flex-direction: column;
	align-items: stretch;
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
`;

const StyledDatePicker = styled(DatePicker)<{ missing?: boolean }>`
	max-width: 50%;
`;

const CountryEditor = styled(CountryDatalist).withConfig({
	shouldForwardProp: (prop) => prop !== "invalid",
})<{ invalid?: boolean }>`
	max-width: 50%;
	.input-wrapper {
		${({ invalid, theme }) => invalid && `border-color: ${theme.colors.red}`};
	}
`;
const TypeSelecter = styled(Selecter).withConfig({
	shouldForwardProp: (prop) => prop !== "missing",
})<{ missing?: boolean }>`
	select {
		${({ missing, theme }) => missing && `border-color: ${theme.colors.red}`};
	}
`;
